import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import throttle from 'lodash/throttle';
import cs from 'classnames';
import { useTranslation } from '@wix/wix-i18n-config';

import { Search } from '../TopBarSearch/Search';
import { useIsInboundVersion } from '../../hooks/useIsInboundVersion';
import { useOnWindowScroll } from '../../hooks/useOnWindowScroll';
import { useSearch } from '../../hooks/useSearch';
import { MegaMenu } from '../MegaMenu/MegaMenu';
import { useInternalRoutes } from '../../hooks/useInternalRoutes';
import { isCategorySlug } from '../../config';
import { useBiLogger } from '../../contexts/BILoggerContext';
import { MegaMenuToggle as MegaMenuToggleV2 } from '../MegaMenu/MegaMenuToggleV2';
import { useTemplatesPageUI } from '../../contexts/TemplatesPageUIContext';
import { Suggestion } from '../SearchSuggestions/SearchSuggestions';
import { usePageScroll } from '../../contexts/PageScrollContext';
import { useFedopsLogger } from '../../contexts/FedopsLoggerContext';
import { MegaMenuV3 } from '../MegaMenu/MegaMenuV3';
import { useExperiments } from '../../contexts/ExperimentsContext';

import s from './TopBar.scss';

const DROPDOWN_OPEN_CLOSE_DELAY = 200;

export const TopBar: FC = () => {
  const { t } = useTranslation();
  const isInboundVer = useIsInboundVersion();
  const [searchValue, setSearchValue] = useState('');
  const { criteria } = useSearch();
  const [isFocusEnabled, setFocusEnabled] = useState(false);
  const navigate = useNavigate();
  const { reportGeneralAction } = useBiLogger();
  const { category, home, search } = useInternalRoutes();
  const {
    isCategoriesMenuOpen,
    toggleCategoriesMenu,
    isTopBarSticky,
    toggleTopBarSticky,
  } = useTemplatesPageUI();
  const { scrollToTop } = usePageScroll();
  const { startCategorySwitchInteraction } = useFedopsLogger();
  const { is } = useExperiments();

  const menuContainerRef = useRef<HTMLDivElement>(null);
  const megaMenuRef = useRef<HTMLDivElement>(null);
  const menuToggleRef = useRef<HTMLDivElement>(null);
  const mainSearchRef = useRef<HTMLInputElement>(null);
  const stickySearchRef = useRef<HTMLInputElement>(null);

  const isNewMegaMenu = is(
    'specs.funnel.StudioTemplatesMegaMenuNewUiV3',
    'true',
  );

  const focusedRef = useMemo(
    () => (isTopBarSticky ? stickySearchRef : mainSearchRef),
    [isTopBarSticky],
  );

  const handleScroll = useCallback(
    throttle(() => {
      const currentRef = menuContainerRef.current;
      if (currentRef) {
        const headerPanelPlace = parseInt(
          window.getComputedStyle(currentRef).top,
          10,
        );
        const headerPanelPositionOnScreen =
          currentRef.getBoundingClientRect().top;

        toggleTopBarSticky(headerPanelPositionOnScreen <= headerPanelPlace);
      }
    }, 100),
    [],
  );

  const handleSearch = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const criteria = e.currentTarget.value;
      if (e.key === 'Enter') {
        if (criteria === '') {
          navigate(home.build());
        } else {
          navigate(search.build(criteria));
        }
        scrollToTop();
      }
    },
    [scrollToTop, navigate, home, search],
  );

  useEffect(() => {
    setSearchValue(criteria);
  }, [criteria]);

  useOnWindowScroll(handleScroll);

  const onSearchChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const onSearchCloseButtonClick = useCallback(() => {
    reportGeneralAction({
      action: 'clear_search',
      criteria: searchValue,
    });
    setSearchValue('');
    navigate(home.build());

    focusedRef.current?.focus();
    scrollToTop();
  }, [
    reportGeneralAction,
    searchValue,
    navigate,
    home,
    scrollToTop,
    focusedRef,
  ]);

  const onFocusSearch = useCallback(() => setFocusEnabled(true), []);
  const onBlurSearch = useCallback(() => setFocusEnabled(false), []);

  const closeTimeoutId = useRef<number>();
  const openTimeoutId = useRef<number>();

  useEffect(() => {
    return () => {
      clearTimeout(openTimeoutId.current);
      clearTimeout(closeTimeoutId.current);
    };
  }, []);

  const closeMenu = () => toggleCategoriesMenu(false);
  const openMenu = () => toggleCategoriesMenu(true);

  const closeMenuWithDelay = () => {
    clearTimeout(openTimeoutId.current);
    closeTimeoutId.current = window.setTimeout(
      closeMenu,
      DROPDOWN_OPEN_CLOSE_DELAY,
    );
  };

  const openMenuWithDelay = () => {
    clearTimeout(closeTimeoutId.current);
    openTimeoutId.current = window.setTimeout(
      openMenu,
      DROPDOWN_OPEN_CLOSE_DELAY,
    );
  };

  const onSuggestionClick = useCallback(
    ({ slug, translationKey }: Suggestion) => {
      reportGeneralAction({
        action: 'popular_searches',
        actionValue: slug,
      });
      const url = isCategorySlug(slug)
        ? category.build(slug)
        : search.build(t(translationKey));

      if (isCategorySlug(slug)) {
        startCategorySwitchInteraction();
      } else {
        focusedRef.current?.focus();
      }

      navigate(url);
      scrollToTop();
    },
    [
      focusedRef,
      navigate,
      reportGeneralAction,
      startCategorySwitchInteraction,
      search,
      t,
      category,
      scrollToTop,
    ],
  );

  useEffect(() => {
    if (isFocusEnabled) {
      focusedRef.current?.focus();
    }
  }, [focusedRef, isFocusEnabled, isTopBarSticky]);

  return (
    <div className={s.topBar}>
      <div className={cs(s.top, { [s.newMegaMenu]: isNewMegaMenu })}>
        <div className={s.titleGroup}>
          <div className={s.title}>
            {isInboundVer
              ? t('app.topBar.title.inbound')
              : t('app.topBar.title.outbound')}
          </div>
        </div>
        <Search
          ref={mainSearchRef}
          className={isTopBarSticky ? s.hiddenSearch : ''}
          withSuggestions
          value={searchValue}
          onChange={onSearchChange}
          onKeyUp={handleSearch}
          onFocus={onFocusSearch}
          onBlur={onBlurSearch}
          onCloseClick={onSearchCloseButtonClick}
          onSuggestionClick={onSuggestionClick}
        />
      </div>
      <div
        className={cs(s.bottom, {
          [s.sticky]: isTopBarSticky,
          [s.newMenu]: isNewMegaMenu && !isTopBarSticky,
        })}
        ref={menuContainerRef}
      >
        {isNewMegaMenu && !isTopBarSticky ? (
          <MegaMenuV3
            onCategoryClick={(slug) => {
              reportGeneralAction({
                action: 'header_browse',
                actionValue: slug,
              });
              startCategorySwitchInteraction();
            }}
          />
        ) : (
          <>
            <div className={s.bottomMargin} />
            <div className={s.bottomContent}>
              <div className={s.mobileMenuToggle} onClick={openMenu}>
                <MegaMenuToggleV2 isActive={isCategoriesMenuOpen} />
              </div>
              <div
                className={s.menuToggle}
                ref={menuToggleRef}
                onMouseEnter={openMenuWithDelay}
                onMouseLeave={(event) => {
                  if (
                    megaMenuRef.current?.contains(event.relatedTarget as Node)
                  ) {
                    return;
                  }

                  closeMenuWithDelay();
                }}
                data-hook="top-bar-menu-toggle"
              >
                <MegaMenuToggleV2 isActive={isCategoriesMenuOpen} />
              </div>
              {isTopBarSticky && (
                <div className={s.stickySearchContainer}>
                  <Search
                    ref={stickySearchRef}
                    className={s.stickySearch}
                    withSuggestions={false}
                    value={searchValue}
                    onChange={onSearchChange}
                    onFocus={onFocusSearch}
                    onBlur={onBlurSearch}
                    onKeyUp={handleSearch}
                    onCloseClick={onSearchCloseButtonClick}
                  />
                </div>
              )}
            </div>
          </>
        )}
        {isNewMegaMenu && !isTopBarSticky && <div className={s.bottomShadow} />}
        <div
          className={s.megaMenu}
          ref={megaMenuRef}
          onMouseEnter={openMenuWithDelay}
          onMouseLeave={(event) => {
            if (menuToggleRef.current?.contains(event.relatedTarget as Node)) {
              return;
            }

            closeMenuWithDelay();
          }}
        >
          <MegaMenu
            onCategoryClick={(slug) => {
              reportGeneralAction({
                action: 'header_browse',
                actionValue: slug,
              });
              startCategorySwitchInteraction();
              closeMenu();
              scrollToTop();
            }}
          />
        </div>
      </div>
    </div>
  );
};
